import { getConfig, safe, sampleRate, state, threshold } from './config'
import { generateUUID } from '@/Utils/uuid'

const JOURNEY_KEY = 'helios.journey'
const STACK_KEY = 'helios.stack'
const CHANNEL_NAME = 'helios'
const STACK_SIZE = 50
const DAY_LIMIT = 5000
const IS_UUID = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i

const memory = {}
const loadId = generateUUID()

let useMemory = false
let journey = null
let loaded = false
let channel = null
let duplicateHandler = null
let sampled = { id: null, rate: null, value: false }

export function storageGet(key) {
  if (!useMemory) {
    try {
      return window.sessionStorage.getItem(key)
    } catch {
      useMemory = true
    }
  }

  return memory[key] ?? null
}

export function storageSet(key, value) {
  if (!useMemory) {
    try {
      window.sessionStorage.setItem(key, value)

      return
    } catch {
      useMemory = true
    }
  }

  memory[key] = value
}

export function storageRemove(key) {
  delete memory[key]

  if (!useMemory) {
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      useMemory = true
    }
  }
}

function readJson(key) {
  try {
    return JSON.parse(storageGet(key) ?? 'null')
  } catch {
    return null
  }
}

function persist() {
  if (journey) {
    storageSet(JOURNEY_KEY, JSON.stringify(journey))
  }
}

function localDay() {
  const date = new Date()

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function load() {
  if (!loaded) {
    loaded = true

    const stored = readJson(JOURNEY_KEY)

    if (stored && IS_UUID.test(stored.id) && Number.isInteger(stored.seq) && Number.isInteger(stored.page_seq)) {
      journey = {
        day: localDay(),
        day_count: 0,
        ...stored,
        user_id: Number.isInteger(stored.user_id) ? stored.user_id : null,
      }
    }
  }

  return journey
}

export function currentJourney() {
  return journey
}

export function journeyId() {
  return journey?.id ?? null
}

export function fraction(id) {
  const value = parseInt(String(id).replace(/-/g, '').slice(-8), 16)

  return Number.isFinite(value) ? value / 0x100000000 : 0
}

export function isSampled() {
  if (!journey) {
    return false
  }

  const rate = sampleRate()

  if (sampled.id !== journey.id || sampled.rate !== rate) {
    sampled = { id: journey.id, rate, value: rate >= 1 || (rate > 0 && fraction(journey.id) < rate) }
    journey.sampled = sampled.value
  }

  return sampled.value
}

export function isActive() {
  return !state.disabled && getConfig() !== null && journey !== null && isSampled()
}

export function isIdle(now = Date.now()) {
  if (!journey) {
    return false
  }

  const last = Math.max(journey.last_at ?? 0, state.lastInputAt ?? 0)

  return now - last > threshold('idle_rotation_minutes') * 60000
}

export function startJourney(reason, previousId, userId) {
  const now = Date.now()

  journey = {
    id: generateUUID(),
    started_at: now,
    last_at: now,
    user_id: userId ?? null,
    seq: 0,
    page_seq: 0,
    sampled: false,
    day: localDay(),
    day_count: 0,
  }
  loaded = true
  isSampled()
  persist()

  return { reason, previous_journey_id: previousId ?? null }
}

export function resumeJourney(userId) {
  const stored = load()

  if (!stored) {
    return startJourney('new', null, userId)
  }

  if (isIdle()) {
    return startJourney('idle', stored.id, userId)
  }

  if (stored.user_id !== null && userId !== null && stored.user_id !== userId) {
    return startJourney('identity', stored.id, userId)
  }

  setJourneyUser(userId)

  return null
}

export function rotateJourney(reason, userId) {
  return startJourney(reason, journey?.id ?? null, userId === undefined ? (journey?.user_id ?? null) : userId)
}

export function setJourneyUser(userId) {
  if (journey && userId !== null && userId !== undefined) {
    journey.user_id = userId
    persist()
  }
}

export function nextSeq() {
  journey.seq += 1
  journey.last_at = Date.now()
  persist()

  return journey.seq
}

export function pageSeq() {
  return journey?.page_seq ?? 0
}

export function nextPageSeq() {
  journey.page_seq += 1
  persist()

  return journey.page_seq
}

export function setPageSeq(value) {
  journey.page_seq = value
  persist()
}

// Per journey per local day: past the limit only errors are kept.
export function allowEvent(isError) {
  const day = localDay()

  if (journey.day !== day) {
    journey.day = day
    journey.day_count = 0
  }

  if (journey.day_count >= DAY_LIMIT && !isError) {
    return false
  }

  journey.day_count += 1

  return true
}

function openChannel() {
  if (channel || typeof window.BroadcastChannel !== 'function') {
    return
  }

  channel = new window.BroadcastChannel(CHANNEL_NAME)
  channel.onmessage = safe((event) => {
    const data = event.data

    if (!data || !journey || data.journey !== journey.id) {
      return
    }

    if (data.type === 'claim' && data.load !== loadId) {
      channel.postMessage({ type: 'taken', journey: data.journey, load: data.load })
    } else if (data.type === 'taken' && data.load === loadId) {
      duplicateHandler?.()
    }
  })
}

// A duplicated tab copies sessionStorage; the tab that already owns the journey answers and this one starts a new one.
export function claimJourney(onDuplicate) {
  duplicateHandler = onDuplicate

  try {
    openChannel()
    channel?.postMessage({ type: 'claim', journey: journey?.id, load: loadId })
  } catch {
    channel = null
  }
}

// An open BroadcastChannel can keep the page out of the bfcache.
export function suspendChannel() {
  const open = channel

  channel = null

  try {
    open?.close()
  } catch {
    return
  }
}

export function resumeChannel() {
  try {
    openChannel()
  } catch {
    channel = null
  }
}

function readStack() {
  const stack = readJson(STACK_KEY)

  return stack && Array.isArray(stack.paths) && Number.isInteger(stack.index) ? stack : { paths: [], index: -1 }
}

function writeStack(stack) {
  storageSet(STACK_KEY, JSON.stringify(stack))
}

export function pushPath(path) {
  const stack = readStack()

  stack.paths = stack.paths.slice(0, stack.index + 1)
  stack.paths.push(path)
  stack.paths = stack.paths.slice(-STACK_SIZE)
  stack.index = stack.paths.length - 1
  writeStack(stack)
}

export function replacePath(path) {
  const stack = readStack()

  if (stack.index < 0) {
    pushPath(path)

    return
  }

  stack.paths[stack.index] = path
  writeStack(stack)
}

export function traversePath(path) {
  const stack = readStack()

  if (stack.index > 0 && stack.paths[stack.index - 1] === path) {
    stack.index -= 1
    writeStack(stack)

    return 'back'
  }

  if (stack.paths[stack.index + 1] === path) {
    stack.index += 1
    writeStack(stack)

    return 'forward'
  }

  return null
}
